import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getRestaurantProfile,
  mergeRemoteBranchIntoProfilePreservingLocalPreferences,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  buildBranchPaymentSetupSnapshot,
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_KIOSK_PAYMENT_METHODS,
  DEFAULT_QR_PAYMENT_METHODS,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
} from "../../services/local/paymentSettings";
import {
  getBranchPublicProfile,
  updateBranchPublicProfile,
} from "../../services/supabase/companyAdminService";
import {
  disconnectMercadoPagoConnection,
  popMercadoPagoConnectFeedback,
  startMercadoPagoConnection,
} from "../../services/supabase/mercadoPagoConnectService";
import { getPublicBranchPaymentSetup } from "../../services/supabase/qrService";
import {
  reverseBranchLocationOnGoogleMaps,
  searchBranchLocationOnGoogleMaps,
} from "../../services/supabase/googleMapsService";

const DIGITAL_PLATFORM_PAYMENT_PROVIDER = "mercado_pago";

const KIOSK_PAYMENT_METHOD_OPTIONS = [
  {
    id: "pay_at_counter",
    label: "Pagar en caja",
    description: "Caja resuelve efectivo, transferencia o tarjeta presencial.",
  },
  {
    id: "pay_by_phone",
    label: "Pagar con QR",
    description:
      "El totem muestra un QR interoperable para pagar desde una billetera virtual compatible.",
  },
];

const QR_PAYMENT_METHOD_OPTIONS = [
  {
    id: "pay_later",
    label: "Pagar despues",
    description:
      "El pedido avanza ahora y el cliente puede pagar despues con el mozo o desde la mesa.",
  },
  {
    id: "pay_now",
    label: "Pagar ahora",
    description:
      "Muestra el checkout desde el celular del comensal para cobrar al momento.",
  },
];

const normalizeProfileText = (value) =>
  value === undefined || value === null ? "" : `${value}`.trim();

const normalizeCoordinate = (value, minimum, maximum) => {
  const normalizedValue = normalizeProfileText(value);
  if (!normalizedValue) {
    return null;
  }

  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum
    ? parsed
    : null;
};

const looksLikeMercadoPagoAppReference = (value) =>
  /^APP_USR-/i.test(normalizeProfileText(value));

const getPaymentProviderStatusLabel = (value) => {
  switch (value) {
    case "pending":
      return "Pendiente";
    case "connected":
      return "Lista para cobrar";
    case "restricted":
      return "Restringida";
    default:
      return "Sin configurar";
  }
};

const reconcileRemotePaymentConnectionState = (localProfile, remoteProfile) => ({
  ...localProfile,
  paymentProvider: normalizePaymentProvider(remoteProfile?.paymentProvider),
  paymentProviderStatus: normalizePaymentProviderStatus(
    remoteProfile?.paymentProviderStatus
  ),
  paymentAccountReference: looksLikeMercadoPagoAppReference(
    remoteProfile?.paymentAccountReference
  )
    ? ""
    : normalizeProfileText(remoteProfile?.paymentAccountReference),
  paymentAccountCbu: normalizeProfileText(remoteProfile?.paymentAccountCbu),
  paymentAccountAlias: normalizeProfileText(remoteProfile?.paymentAccountAlias),
  paymentLocationCity: normalizeProfileText(remoteProfile?.paymentLocationCity),
  paymentLocationState: normalizeProfileText(remoteProfile?.paymentLocationState),
  paymentLocationLatitude: normalizeProfileText(
    remoteProfile?.paymentLocationLatitude
  ),
  paymentLocationLongitude: normalizeProfileText(
    remoteProfile?.paymentLocationLongitude
  ),
  paymentLocationReference: normalizeProfileText(
    remoteProfile?.paymentLocationReference
  ),
  kioskNativeQrReady: remoteProfile?.kioskNativeQrReady === true,
});

const buildRemotePaymentPayload = (profile = {}, branch = {}) => {
  const branding = profile?.branding || {};

  return {
    name: normalizeProfileText(profile?.nombre || profile?.name || branch?.name),
    phone: normalizeProfileText(
      profile?.telefono || profile?.phone || branch?.phone
    ),
    address: normalizeProfileText(
      profile?.direccion || profile?.address || branch?.address
    ),
    description: normalizeProfileText(
      profile?.descripcion || profile?.description || branch?.description
    ),
    // updateBranchPublicProfile writes branding and settings together, so this
    // panel must preserve every existing asset while changing payment fields.
    logoUrl: normalizeProfileText(
      profile?.logoUrl || branding?.logoCompactUrl || branding?.logoPrimaryUrl
    ),
    logoCompactUrl: normalizeProfileText(
      profile?.logoCompactUrl || branding?.logoCompactUrl
    ),
    iconLogoUrl: normalizeProfileText(
      profile?.iconLogoUrl || branding?.iconLogoUrl
    ),
    loadingLogoUrl: normalizeProfileText(
      profile?.loadingLogoUrl || branding?.loadingLogoUrl
    ),
    coverUrl: normalizeProfileText(profile?.imagenPortada || profile?.coverUrl),
    kioskDineInImageUrl: normalizeProfileText(profile?.kioskDineInImageUrl),
    kioskTakeawayImageUrl: normalizeProfileText(profile?.kioskTakeawayImageUrl),
    coverDisplayMode: profile?.coverDisplayMode || "stretch",
    primaryColor:
      normalizeProfileText(profile?.primaryColor || branding?.primaryColor) ||
      "#2563eb",
    secondaryColor:
      normalizeProfileText(profile?.secondaryColor || branding?.secondaryColor) ||
      "#f8fafc",
    fontFamily: normalizeProfileText(profile?.fontFamily || branding?.fontFamily),
    loadingAnimation:
      profile?.loadingAnimation || branding?.loadingAnimation || "spin",
    welcomeMessage: normalizeProfileText(
      profile?.welcomeMessage || profile?.mensajeBienvenida
    ),
    kitchenMode: profile?.kitchenMode,
    serviceMode: profile?.serviceMode,
    stockEnabled: profile?.stockEnabled !== false,
    waiterEnabled: profile?.waiterEnabled !== false,
    aiEnabled: profile?.aiEnabled !== false,
    kioskMenuMode: profile?.kioskMenuMode,
    kioskStationCount: profile?.kioskStationCount,
    paymentProvider: normalizePaymentProvider(
      profile?.paymentProvider || DEFAULT_BRANCH_PAYMENT_PROVIDER
    ),
    paymentProviderStatus: normalizePaymentProviderStatus(
      profile?.paymentProviderStatus || DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
    ),
    paymentAccountReference: looksLikeMercadoPagoAppReference(
      profile?.paymentAccountReference
    )
      ? ""
      : normalizeProfileText(profile?.paymentAccountReference),
    paymentAccountCbu: normalizeProfileText(profile?.paymentAccountCbu),
    paymentAccountAlias: normalizeProfileText(profile?.paymentAccountAlias),
    paymentLocationCity: normalizeProfileText(profile?.paymentLocationCity),
    paymentLocationState: normalizeProfileText(profile?.paymentLocationState),
    paymentLocationLatitude: normalizeProfileText(
      profile?.paymentLocationLatitude
    ),
    paymentLocationLongitude: normalizeProfileText(
      profile?.paymentLocationLongitude
    ),
    paymentLocationReference: normalizeProfileText(
      profile?.paymentLocationReference
    ),
    kioskNativeQrReady: profile?.kioskNativeQrReady === true,
    commissionRate: profile?.commissionRate,
    paymentSplitMode: profile?.paymentSplitMode,
    kioskPaymentMethods: normalizePaymentMethods(
      profile?.kioskPaymentMethods || DEFAULT_KIOSK_PAYMENT_METHODS,
      "kiosk"
    ),
    qrPaymentMethods: normalizePaymentMethods(
      profile?.qrPaymentMethods || DEFAULT_QR_PAYMENT_METHODS,
      "qr"
    ),
  };
};

function BranchDigitalPaymentsPanel({
  branch,
  disabled = false,
  title = "Cobros y cuenta digital",
  currentAccessRoleLabel = "Este perfil",
}) {
  const navigate = useNavigate();
  const { isCreatorMode, isSupabaseConfigured } = useAuth();
  const [profile, setProfile] = useState(() => getRestaurantProfile(branch));
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("neutral");
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [connectingMercadoPago, setConnectingMercadoPago] = useState(false);
  const [disconnectingMercadoPago, setDisconnectingMercadoPago] = useState(false);
  const [runtimePaymentSetup, setRuntimePaymentSetup] = useState(null);
  const [mercadoPagoOauthSession, setMercadoPagoOauthSession] = useState(null);
  const [locationQuery, setLocationQuery] = useState(() =>
    normalizeProfileText(
      getRestaurantProfile(branch)?.direccion || branch?.address
    )
  );
  const [locatingBranch, setLocatingBranch] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");
  const [locationTone, setLocationTone] = useState("neutral");
  const mercadoPagoOauthPanelRef = useRef(null);

  const branchName = useMemo(
    () =>
      normalizeProfileText(profile?.nombre || branch?.name) || "Sucursal activa",
    [branch?.name, profile?.nombre]
  );

  const branchCoordinates = useMemo(() => {
    const latitude = normalizeCoordinate(
      profile?.paymentLocationLatitude,
      -90,
      90
    );
    const longitude = normalizeCoordinate(
      profile?.paymentLocationLongitude,
      -180,
      180
    );

    return latitude !== null && longitude !== null
      ? { latitude, longitude }
      : null;
  }, [profile?.paymentLocationLatitude, profile?.paymentLocationLongitude]);

  const googleMapsQuery = useMemo(
    () =>
      branchCoordinates
        ? `${branchCoordinates.latitude},${branchCoordinates.longitude}`
        : normalizeProfileText(
            locationQuery || profile?.direccion || branch?.address
          ),
    [
      branch?.address,
      branchCoordinates,
      locationQuery,
      profile?.direccion,
    ]
  );

  const googleMapsEmbedUrl = useMemo(
    () =>
      googleMapsQuery
        ? `https://www.google.com/maps?q=${encodeURIComponent(
            googleMapsQuery
          )}&z=16&output=embed`
        : "",
    [googleMapsQuery]
  );

  const googleMapsExternalUrl = useMemo(
    () =>
      googleMapsQuery
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            googleMapsQuery
          )}`
        : "https://www.google.com/maps",
    [googleMapsQuery]
  );

  const normalizedKioskPaymentMethods = useMemo(
    () => normalizePaymentMethods(profile?.kioskPaymentMethods, "kiosk"),
    [profile?.kioskPaymentMethods]
  );

  const normalizedQrPaymentMethods = useMemo(
    () => normalizePaymentMethods(profile?.qrPaymentMethods, "qr"),
    [profile?.qrPaymentMethods]
  );

  const selectedPaymentProvider = useMemo(
    () =>
      normalizePaymentProvider(
        profile?.paymentProvider || DEFAULT_BRANCH_PAYMENT_PROVIDER
      ),
    [profile?.paymentProvider]
  );

  const isMercadoPagoSelected =
    selectedPaymentProvider === DIGITAL_PLATFORM_PAYMENT_PROVIDER;

  const digitalPaymentsRequireMercadoPago = useMemo(
    () =>
      normalizedKioskPaymentMethods.includes("pay_by_phone") ||
      normalizedQrPaymentMethods.includes("pay_now"),
    [normalizedKioskPaymentMethods, normalizedQrPaymentMethods]
  );

  const refreshRuntimePaymentSetup = useCallback(async () => {
    if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
      setRuntimePaymentSetup(null);
      return null;
    }

    try {
      const nextSetup = await getPublicBranchPaymentSetup(branch.id);
      setRuntimePaymentSetup(nextSetup);
      return nextSetup;
    } catch (error) {
      setRuntimePaymentSetup(null);
      return null;
    }
  }, [branch?.id, isCreatorMode, isSupabaseConfigured]);

  const kioskPaymentSetup = useMemo(
    () =>
      buildBranchPaymentSetupSnapshot({
        ...profile,
        address: normalizeProfileText(profile?.direccion || branch?.address),
        paymentProvider:
          runtimePaymentSetup?.paymentProvider || profile?.paymentProvider,
        paymentProviderStatus:
          runtimePaymentSetup?.paymentProviderStatus ||
          profile?.paymentProviderStatus,
        paymentAccountReference:
          runtimePaymentSetup?.paymentAccountReference ||
          profile?.paymentAccountReference,
        paymentAccountCbu:
          runtimePaymentSetup?.paymentAccountCbu || profile?.paymentAccountCbu,
        paymentAccountAlias:
          runtimePaymentSetup?.paymentAccountAlias ||
          profile?.paymentAccountAlias,
        manualOperationsRuleEnabled:
          runtimePaymentSetup?.manualOperationsRuleEnabled,
        manualOperationUnitsCost: runtimePaymentSetup?.manualOperationUnitsCost,
        manualOperationsAvailable:
          runtimePaymentSetup?.manualOperationsAvailable,
        manualOperationsLowBalance:
          runtimePaymentSetup?.manualOperationsLowBalance,
        manualOperationsBlockingReason:
          runtimePaymentSetup?.manualOperationsBlockingReason,
        operationalAccessAllowed:
          runtimePaymentSetup?.operationalAccessAllowed,
        operationalAccessMode: runtimePaymentSetup?.operationalAccessMode,
        operationalBlockingReason:
          runtimePaymentSetup?.operationalBlockingReason,
        kioskPaymentMethods: normalizedKioskPaymentMethods,
        qrPaymentMethods: normalizedQrPaymentMethods,
      }),
    [
      branch?.address,
      normalizedKioskPaymentMethods,
      normalizedQrPaymentMethods,
      profile,
      runtimePaymentSetup,
    ]
  );

  const notifyPaymentsPermissionDenied = useCallback(
    (actionLabel) => {
      if (!disabled) {
        return true;
      }

      setStatusTone("error");
      setStatusMessage(
        `${currentAccessRoleLabel} puede revisar cobros, pero no ${actionLabel} en esta sucursal.`
      );
      return false;
    },
    [currentAccessRoleLabel, disabled]
  );

  const updateDraft = useCallback(
    (updater, actionLabel = "editar la configuracion de cobros") => {
      if (!notifyPaymentsPermissionDenied(actionLabel)) {
        return false;
      }

      setProfile((prev) =>
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater }
      );
      return true;
    },
    [notifyPaymentsPermissionDenied]
  );

  const applyGoogleMapsLocation = useCallback(
    (location) => {
      const latitude = Number(location?.latitude);
      const longitude = Number(location?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error("Google Maps no devolvio coordenadas validas.");
      }

      const formattedAddress =
        normalizeProfileText(location?.formattedAddress) ||
        normalizeProfileText(locationQuery);

      updateDraft(
        (prev) => ({
          ...prev,
          direccion: formattedAddress || prev?.direccion || "",
          paymentLocationCity:
            normalizeProfileText(location?.city) ||
            prev?.paymentLocationCity ||
            "",
          paymentLocationState:
            normalizeProfileText(location?.state) ||
            prev?.paymentLocationState ||
            "",
          paymentLocationLatitude: latitude.toFixed(6),
          paymentLocationLongitude: longitude.toFixed(6),
        }),
        "cambiar la ubicacion de la sucursal"
      );

      setLocationQuery(formattedAddress);
      setLocationTone("success");
      setLocationMessage(
        "Ubicacion encontrada. Presiona Guardar cobros para confirmar el cambio."
      );
    },
    [locationQuery, updateDraft]
  );

  const handleSearchBranchLocation = useCallback(
    async (event) => {
      event?.preventDefault?.();

      if (!notifyPaymentsPermissionDenied("cambiar la ubicacion de la sucursal")) {
        return;
      }

      const query = normalizeProfileText(locationQuery);
      if (query.length < 5) {
        setLocationTone("error");
        setLocationMessage(
          "Escribe calle, altura, ciudad y provincia para ubicar bien el local."
        );
        return;
      }

      if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
        setLocationTone("error");
        setLocationMessage(
          "La busqueda automatica necesita una sucursal guardada y una sesion conectada."
        );
        return;
      }

      setLocatingBranch(true);
      setLocationMessage("");

      try {
        const location = await searchBranchLocationOnGoogleMaps({
          branchId: branch.id,
          query,
        });
        applyGoogleMapsLocation(location);
      } catch (error) {
        setLocationTone("error");
        setLocationMessage(
          error.message || "No pudimos encontrar esa ubicacion en Google Maps."
        );
      } finally {
        setLocatingBranch(false);
      }
    },
    [
      applyGoogleMapsLocation,
      branch?.id,
      isCreatorMode,
      isSupabaseConfigured,
      locationQuery,
      notifyPaymentsPermissionDenied,
    ]
  );

  const handleUseCurrentLocation = useCallback(async () => {
    if (!notifyPaymentsPermissionDenied("cambiar la ubicacion de la sucursal")) {
      return;
    }

    if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
      setLocationTone("error");
      setLocationMessage(
        "La ubicacion automatica necesita una sucursal guardada y una sesion conectada."
      );
      return;
    }

    if (!navigator.geolocation) {
      setLocationTone("error");
      setLocationMessage("Este dispositivo no permite obtener la ubicacion actual.");
      return;
    }

    setLocatingBranch(true);
    setLocationMessage("Solicitando la ubicacion de este dispositivo...");
    setLocationTone("neutral");

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 30000,
        });
      });
      const location = await reverseBranchLocationOnGoogleMaps({
        branchId: branch.id,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      applyGoogleMapsLocation(location);
    } catch (error) {
      setLocationTone("error");
      setLocationMessage(
        error?.code === 1
          ? "No se autorizo la ubicacion. Puedes buscar el domicilio escribiendolo arriba."
          : error.message || "No pudimos usar la ubicacion de este dispositivo."
      );
    } finally {
      setLocatingBranch(false);
    }
  }, [
    applyGoogleMapsLocation,
    branch?.id,
    isCreatorMode,
    isSupabaseConfigured,
    notifyPaymentsPermissionDenied,
  ]);

  const syncRemoteToDraft = useCallback(
    async ({ successMessage = "", silent = false } = {}) => {
      if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
        return null;
      }

      const remoteProfile = await getBranchPublicProfile(branch.id);
      const localProfile = getRestaurantProfile(branch);
      const mergedProfile = saveRestaurantProfile(
        branch,
        mergeRemoteBranchIntoProfilePreservingLocalPreferences(
          reconcileRemotePaymentConnectionState(localProfile, remoteProfile),
          remoteProfile
        )
      );

      setProfile(mergedProfile);
      setLocationQuery(
        normalizeProfileText(
          mergedProfile?.direccion || remoteProfile?.address || branch?.address
        )
      );

      if (!silent && successMessage) {
        setStatusTone("success");
        setStatusMessage(successMessage);
      }

      return mergedProfile;
    },
    [branch, isCreatorMode, isSupabaseConfigured]
  );

  useEffect(() => {
    const localProfile = getRestaurantProfile(branch);
    setProfile(localProfile);
    setLocationQuery(
      normalizeProfileText(localProfile?.direccion || branch?.address)
    );
    setLocationMessage("");
    setLocationTone("neutral");
    setStatusMessage("");
    setStatusTone("neutral");
    setMercadoPagoOauthSession(null);
    setConnectingMercadoPago(false);
    setDisconnectingMercadoPago(false);

    let active = true;

    if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
      return () => {
        active = false;
      };
    }

    setLoadingRemote(true);

    const loadRemoteProfile = async () => {
      try {
        const [mergedProfile] = await Promise.all([
          syncRemoteToDraft({ silent: true }),
          refreshRuntimePaymentSetup(),
        ]);

        if (!active || !mergedProfile) {
          return;
        }
      } catch (error) {
        if (!active) {
          return;
        }

        setStatusTone("error");
        setStatusMessage(
          error.message ||
            "No pudimos leer el estado remoto de cobros para esta sucursal."
        );
      } finally {
        if (active) {
          setLoadingRemote(false);
        }
      }
    };

    loadRemoteProfile();

    return () => {
      active = false;
    };
  }, [
    branch,
    branch?.id,
    isCreatorMode,
    isSupabaseConfigured,
    refreshRuntimePaymentSetup,
    syncRemoteToDraft,
  ]);

  useEffect(() => {
    const feedback = popMercadoPagoConnectFeedback();

    if (!feedback) {
      return;
    }

    const currentBranchId = String(branch?.id || "").trim();

    if (feedback.branchId && currentBranchId && feedback.branchId !== currentBranchId) {
      return;
    }

    setConnectingMercadoPago(false);

    if (feedback.type === "success") {
      setMercadoPagoOauthSession(null);
      syncRemoteToDraft({
        successMessage:
          feedback.message || "La cuenta de Mercado Pago ya quedo conectada.",
      })
        .then(() => refreshRuntimePaymentSetup())
        .catch((error) => {
        setStatusTone("error");
        setStatusMessage(
          error.message ||
            "La autorizacion termino, pero no pudimos refrescar la sucursal."
        );
        });
      return;
    }

    setStatusTone("error");
    setStatusMessage(
      feedback.message || "No se pudo completar la vinculacion con Mercado Pago."
    );
  }, [branch?.id, refreshRuntimePaymentSetup, syncRemoteToDraft]);

  useEffect(() => {
    if (!mercadoPagoOauthSession?.authorizationUrl || !mercadoPagoOauthPanelRef.current) {
      return;
    }

    window.requestAnimationFrame(() => {
      mercadoPagoOauthPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [mercadoPagoOauthSession]);

  useEffect(() => {
    if (!branch?.id || !mercadoPagoOauthSession?.authorizationUrl) {
      return undefined;
    }

    if (kioskPaymentSetup.providerConnected) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timerId = null;

    const pollRemoteConnection = async () => {
      attempts += 1;

      try {
        const remoteProfile = await getBranchPublicProfile(branch.id);

        if (cancelled) {
          return;
        }

        const remoteProvider = normalizePaymentProvider(remoteProfile?.paymentProvider);
        const remoteStatus = normalizePaymentProviderStatus(
          remoteProfile?.paymentProviderStatus
        );

        if (
          remoteProvider === DIGITAL_PLATFORM_PAYMENT_PROVIDER &&
          remoteStatus === "connected"
        ) {
          const mergedProfile = saveRestaurantProfile(
            branch,
            mergeRemoteBranchIntoProfilePreservingLocalPreferences(
              reconcileRemotePaymentConnectionState(
                getRestaurantProfile(branch),
                remoteProfile
              ),
              remoteProfile
            )
          );

          setProfile(mergedProfile);
          void refreshRuntimePaymentSetup();
          setMercadoPagoOauthSession(null);
          setConnectingMercadoPago(false);
          setStatusTone("success");
          setStatusMessage(
            remoteProfile?.paymentAccountReference
              ? `La cuenta ${remoteProfile.paymentAccountReference} ya quedo conectada a MozzoApp.`
              : "La cuenta de Mercado Pago ya quedo conectada a MozzoApp."
          );
          return;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
      }

      if (cancelled || attempts >= 40) {
        if (!cancelled) {
          setConnectingMercadoPago(false);
          setStatusTone("error");
          setStatusMessage(
            "El QR se genero, pero la sucursal sigue pendiente. Si ya autorizaste Mercado Pago en el celular, vuelve a intentar o revisamos el callback."
          );
        }
        return;
      }

      timerId = window.setTimeout(pollRemoteConnection, 3000);
    };

    timerId = window.setTimeout(pollRemoteConnection, 2500);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    branch,
    branch?.id,
    kioskPaymentSetup.providerConnected,
    mercadoPagoOauthSession,
    refreshRuntimePaymentSetup,
  ]);

  useEffect(() => {
    if (!digitalPaymentsRequireMercadoPago || isMercadoPagoSelected) {
      return;
    }

    setProfile((prev) => ({
      ...prev,
      paymentProvider: DIGITAL_PLATFORM_PAYMENT_PROVIDER,
    }));
  }, [digitalPaymentsRequireMercadoPago, isMercadoPagoSelected]);

  const togglePaymentMethod = useCallback(
    (channel, methodId) => {
      if (!notifyPaymentsPermissionDenied("cambiar los medios de pago visibles")) {
        return;
      }

      const currentMethods =
        channel === "qr" ? normalizedQrPaymentMethods : normalizedKioskPaymentMethods;

      const nextMethods = currentMethods.includes(methodId)
        ? currentMethods.length > 1
          ? currentMethods.filter((entry) => entry !== methodId)
          : currentMethods
        : [...currentMethods, methodId];

      updateDraft((prev) => ({
        ...prev,
        ...(channel === "qr"
          ? { qrPaymentMethods: nextMethods }
          : { kioskPaymentMethods: nextMethods }),
      }));
    },
    [
      normalizedKioskPaymentMethods,
      normalizedQrPaymentMethods,
      notifyPaymentsPermissionDenied,
      updateDraft,
    ]
  );

  const handleSavePaymentSettings = useCallback(async () => {
    if (!notifyPaymentsPermissionDenied("guardar la configuracion de cobros")) {
      return;
    }

    if (!branch?.id) {
      setStatusTone("error");
      setStatusMessage("No encontramos una sucursal activa para guardar los cobros.");
      return;
    }

    setSavingSettings(true);
    setStatusMessage("");

    try {
      const localProfile = saveRestaurantProfile(branch, {
        ...profile,
        kioskPaymentMethods: normalizedKioskPaymentMethods,
        qrPaymentMethods: normalizedQrPaymentMethods,
      });

      let nextProfile = localProfile;

      if (!isCreatorMode && isSupabaseConfigured) {
        const remoteProfile = await updateBranchPublicProfile(
          branch.id,
          buildRemotePaymentPayload(localProfile, branch)
        );

        nextProfile = saveRestaurantProfile(
          branch,
          mergeRemoteBranchIntoProfilePreservingLocalPreferences(
            localProfile,
            remoteProfile
          )
        );
      }

      setProfile(nextProfile);
      await refreshRuntimePaymentSetup();
      setStatusTone("success");
      setStatusMessage("Cobros y metodos digitales guardados.");
      if (branchCoordinates) {
        setLocationTone("success");
        setLocationMessage("Ubicacion de la sucursal guardada correctamente.");
      }
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(
        error.message || "No se pudo guardar la configuracion de cobros."
      );
    } finally {
      setSavingSettings(false);
    }
  }, [
    branch,
    branchCoordinates,
    isCreatorMode,
    isSupabaseConfigured,
    notifyPaymentsPermissionDenied,
    normalizedKioskPaymentMethods,
    normalizedQrPaymentMethods,
    profile,
    refreshRuntimePaymentSetup,
  ]);

  const handleStartMercadoPagoConnection = useCallback(async () => {
    if (!notifyPaymentsPermissionDenied("conectar Mercado Pago")) {
      return;
    }

    if (!branch?.id) {
      setStatusTone("error");
      setStatusMessage(
        "No encontramos la sucursal activa para iniciar la vinculacion con Mercado Pago."
      );
      return;
    }

    const confirmed = window.confirm(
      "Mercado Pago va a vincular la cuenta que inicie sesion en la ventana que se abre ahora. Si ahi entras con tu cuenta personal, quedara vinculada tu cuenta personal. Si quieres vincular la cuenta del restaurante, continua solo si vas a iniciar sesion con esa cuenta."
    );

    if (!confirmed) {
      return;
    }

    setConnectingMercadoPago(true);
    setStatusMessage("");

    try {
      const result = await Promise.race([
        startMercadoPagoConnection({
          branchId: branch.id,
          branchName,
          returnToPath: "/app/configuracion",
        }),
        new Promise((_, reject) =>
          window.setTimeout(() => {
            reject(
              new Error(
                "No pudimos generar el QR a tiempo. Intenta otra vez y, si sigue igual, revisamos el backend OAuth."
              )
            );
          }, 15000)
        ),
      ]);

      const nextProfile = saveRestaurantProfile(branch, {
        ...profile,
        paymentProvider: DIGITAL_PLATFORM_PAYMENT_PROVIDER,
        paymentProviderStatus:
          profile?.paymentProviderStatus === "connected" ? "connected" : "pending",
      });

      setProfile(nextProfile);
      setMercadoPagoOauthSession({
        authorizationUrl: result.authorizationUrl,
        branchName,
      });
      setStatusTone("success");
      setStatusMessage(
        "Escanea el QR con el celular que tenga la cuenta del negocio para autorizar Mercado Pago."
      );
      setConnectingMercadoPago(false);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(
        error.message || "No se pudo iniciar la conexion con Mercado Pago."
      );
      setConnectingMercadoPago(false);
    }
  }, [branch, branchName, notifyPaymentsPermissionDenied, profile]);

  const handleDisconnectMercadoPagoConnection = useCallback(async () => {
    if (!notifyPaymentsPermissionDenied("desenlazar Mercado Pago")) {
      return;
    }

    if (!branch?.id) {
      setStatusTone("error");
      setStatusMessage(
        "No encontramos la sucursal activa para desenlazar la cuenta de Mercado Pago."
      );
      return;
    }

    const confirmed = window.confirm(
      "Esto quitara de MozzoApp la cuenta Mercado Pago conectada para esta sucursal y desactivara el cobro digital del kiosk. Podras conectar otra cuenta despues. Quieres continuar?"
    );

    if (!confirmed) {
      return;
    }

    setDisconnectingMercadoPago(true);
    setStatusMessage("");

    try {
      const result = await disconnectMercadoPagoConnection({
        branchId: branch.id,
      });

      const clearedProfile = saveRestaurantProfile(branch, {
        ...profile,
        paymentProvider: DEFAULT_BRANCH_PAYMENT_PROVIDER,
        paymentProviderStatus: "not_configured",
        paymentAccountReference: "",
        paymentAccountCbu: "",
        paymentAccountAlias: "",
        kioskNativeQrReady: false,
      });

      setProfile(clearedProfile);
      setMercadoPagoOauthSession(null);
      setStatusTone("success");
      setStatusMessage(
        result.message || "La cuenta de Mercado Pago quedo desenlazada."
      );

      await Promise.all([
        syncRemoteToDraft({ silent: true }),
        refreshRuntimePaymentSetup(),
      ]);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(
        error.message || "No se pudo desenlazar la cuenta de Mercado Pago."
      );
    } finally {
      setDisconnectingMercadoPago(false);
    }
  }, [
    branch,
    notifyPaymentsPermissionDenied,
    profile,
    refreshRuntimePaymentSetup,
    syncRemoteToDraft,
  ]);

  const handleOpenMercadoPagoOauthInBrowser = useCallback(() => {
    if (!notifyPaymentsPermissionDenied("abrir la vinculacion de Mercado Pago")) {
      return;
    }

    const authorizationUrl = normalizeProfileText(
      mercadoPagoOauthSession?.authorizationUrl
    );

    if (!authorizationUrl) {
      return;
    }

    window.open(authorizationUrl, "_blank", "noopener,noreferrer");
  }, [mercadoPagoOauthSession, notifyPaymentsPermissionDenied]);

  const handleCloseMercadoPagoOauthSession = useCallback(() => {
    setMercadoPagoOauthSession(null);
  }, []);

  if (!branch) {
    return (
      <article style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h3 style={styles.panelTitle}>{title}</h3>
            <span style={styles.helperText}>
              Primero selecciona o crea una sucursal para administrar cobros.
            </span>
          </div>
        </div>
      </article>
    );
  }

  const showStatusBanner = loadingRemote || statusMessage;

  return (
    <article style={styles.panel}>
      <div style={styles.panelHeader}>
        <div style={styles.headerCopy}>
          <h3 style={styles.panelTitle}>{title}</h3>
          <span style={styles.helperText}>
            Aqui conectas la cuenta comercial que recibira los cobros digitales
            de la sucursal activa. La empresa administra esto desde Configuracion
            para no mezclarlo con la edicion visual del restaurante.
          </span>
        </div>
        <div style={styles.branchBadge}>
          <span style={styles.branchBadgeLabel}>Sucursal activa</span>
          <strong style={styles.branchBadgeValue}>{branchName}</strong>
        </div>
      </div>

      {showStatusBanner ? (
        <div
          style={{
            ...styles.notice,
            ...(loadingRemote
              ? styles.noticeNeutral
              : statusTone === "error"
                ? styles.noticeError
                : styles.noticeOk),
            marginBottom: "18px",
          }}
        >
          {loadingRemote
            ? "Revisando el estado remoto de la cuenta digital..."
            : statusMessage}
        </div>
      ) : null}

      {disabled ? (
        <div style={{ ...styles.notice, ...styles.noticeWarning }}>
          {currentAccessRoleLabel} puede revisar el estado de cobros, pero no conectar
          cuentas ni cambiar medios de pago desde esta sucursal.
        </div>
      ) : null}

      <div style={styles.paymentConnectionCard}>
        <div style={styles.paymentConnectionCopy}>
          <strong style={styles.paymentGroupTitle}>
            Vinculacion de la cuenta del restaurante
          </strong>
          <span style={styles.helperText}>
            MozzoApp opera como plataforma y hoy usa Mercado Pago como base de
            cobro digital. El restaurante no necesita entrar a Developer ni crear
            credenciales: solo autoriza su cuenta una vez y queda lista para usarla
            en Menu QR y kiosk.
          </span>
          <span style={styles.paymentConnectionStatus}>
            {kioskPaymentSetup.providerConnected
              ? "La cuenta del restaurante ya figura conectada a MozzoApp. Con eso puedes habilitar 'Pagar ahora' en Menu QR y 'Pagar con QR' en kiosk."
              : digitalPaymentsRequireMercadoPago
                ? "Los cobros digitales ya quedaron marcados en esta sucursal. Solo falta autorizar la cuenta Mercado Pago del restaurante para empezar a cobrar."
                : "Puedes conectar la cuenta ahora y dejar preparada la sucursal para cuando actives cobro digital en Menu QR o kiosk."}
          </span>
        </div>

        <div style={styles.paymentConnectionActions}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={handleStartMercadoPagoConnection}
            disabled={
              disabled ||
              savingSettings ||
              connectingMercadoPago ||
              disconnectingMercadoPago
            }
          >
            {connectingMercadoPago
              ? "Generando QR..."
              : kioskPaymentSetup.providerConnected
                ? "Reconectar Mercado Pago"
                : "Conectar Mercado Pago"}
          </button>
          {kioskPaymentSetup.providerConnected ? (
            <button
              type="button"
              style={styles.miniDangerButton}
              onClick={handleDisconnectMercadoPagoConnection}
              disabled={
                disabled ||
                savingSettings ||
                connectingMercadoPago ||
                disconnectingMercadoPago
              }
            >
              {disconnectingMercadoPago ? "Desenlazando..." : "Desenlazar cuenta"}
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          ...styles.notice,
          ...(kioskPaymentSetup.operationalAccessMode === "blocked"
            ? styles.noticeError
            : kioskPaymentSetup.manualOperationsLowBalance
              ? styles.noticeWarning
              : styles.noticeOk),
        }}
      >
        <span>
          {kioskPaymentSetup.operationalAccessMode === "blocked"
            ? kioskPaymentSetup.operationalBlockingReason ||
              "La sucursal quedo bloqueada hasta comprar creditos MZ."
            : kioskPaymentSetup.operationalAccessMode === "credits_only"
              ? "Mercado Pago todavia no esta conectado. La sucursal puede operar solo con cobros manuales respaldados por creditos MZ hasta agotar ese cupo."
              : kioskPaymentSetup.manualOperationsLowBalance
                ? "Mercado Pago esta listo, pero el saldo MZ esta bajo. Si siguen vendiendo, la cuenta se bloqueara hasta recargar creditos MZ."
                : "La sucursal ya puede operar. Los cobros digitales pasan por Mercado Pago y los cobros manuales seguiran consumiendo creditos MZ cuando no usen la integracion."}
        </span>
        <button
          type="button"
          style={styles.mzPurchaseButton}
          onClick={() => navigate("/app/pagos/mz")}
          disabled={disabled}
        >
          Comprar MZ
        </button>
      </div>

      {mercadoPagoOauthSession?.authorizationUrl ? (
        <div ref={mercadoPagoOauthPanelRef} style={styles.oauthQrPanel}>
          <div style={styles.oauthQrCopy}>
            <strong style={styles.panelTitle}>Escanea para vincular Mercado Pago</strong>
            <span style={styles.helperText}>
              Pedrito debe escanear este QR con el celular donde tenga iniciada
              la cuenta de Mercado Pago del negocio. El QR abre la autorizacion
              OAuth oficial de MozzoApp para esta sucursal.
            </span>
            <span style={styles.paymentConnectionStatus}>
              Sucursal: {branchName}
            </span>
          </div>

          <div style={styles.oauthQrBody}>
            <div style={styles.oauthQrCanvas}>
              <QRCode
                value={mercadoPagoOauthSession.authorizationUrl}
                size={220}
                level="M"
              />
            </div>

            <div style={styles.oauthQrActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={handleOpenMercadoPagoOauthInBrowser}
                disabled={disabled}
              >
                Abrir en este navegador
              </button>
              <button
                type="button"
                style={styles.miniDangerButton}
                onClick={handleCloseMercadoPagoOauthSession}
              >
                Cerrar QR
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div style={styles.fieldGrid}>
        <label style={styles.field}>
          <span style={styles.label}>Modelo de cobro digital</span>
          <input
            type="text"
            value="MozzoApp + Mercado Pago"
            readOnly
            style={styles.input}
          />
          <span style={styles.helperText}>
            MozzoApp usa Mercado Pago como base operativa y, en kiosk, muestra un
            QR que abre el checkout en el celular del cliente.
          </span>
          <span style={styles.helperText}>
            Para el comensal queda indicado escanearlo con la camara del celular,
            no desde la app de Mercado Pago.
          </span>
        </label>

        <label style={styles.field}>
          <span style={styles.label}>Estado de la vinculacion</span>
          <input
            type="text"
            value={getPaymentProviderStatusLabel(
              profile?.paymentProviderStatus || DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
            )}
            readOnly
            style={styles.input}
          />
          <span style={styles.helperText}>
            Este estado lo actualiza MozzoApp cuando la cuenta se autoriza de
            verdad. El restaurante no tiene que marcarlo manualmente.
          </span>
        </label>

        <label style={styles.field}>
          <span style={styles.label}>Referencia de la cuenta</span>
          <input
            type="text"
            value={profile?.paymentAccountReference || ""}
            onChange={(event) =>
              updateDraft((prev) => ({
                ...prev,
                paymentAccountReference: event.target.value,
              }))
            }
            style={styles.input}
            placeholder="Usuario, email o identificador del proveedor"
            disabled={disabled}
          />
          <span style={styles.helperText}>
            Cuando Mercado Pago quede conectado, este dato puede completarse
            automaticamente con la referencia real de la cuenta.
          </span>
        </label>

        <div style={{ ...styles.field, gridColumn: "1 / -1" }}>
          <span style={styles.label}>Comision MozzoApp</span>
          <span style={styles.helperText}>
            La comision se administra internamente desde MozzoApp y no la define
            el restaurante en esta pantalla. Aqui solo se conecta la cuenta que
            va a cobrar y liquidar los pagos digitales.
          </span>
        </div>

        <label style={styles.field}>
          <span style={styles.label}>CBU de liquidacion</span>
          <input
            type="text"
            value={profile?.paymentAccountCbu || ""}
            onChange={(event) =>
              updateDraft((prev) => ({
                ...prev,
                paymentAccountCbu: event.target.value,
              }))
            }
            style={styles.input}
            placeholder="Solo digitos"
            disabled={disabled}
          />
        </label>

        <label style={styles.field}>
          <span style={styles.label}>Alias / nombre visible</span>
          <input
            type="text"
            value={profile?.paymentAccountAlias || ""}
            onChange={(event) =>
              updateDraft((prev) => ({
                ...prev,
                paymentAccountAlias: event.target.value,
              }))
            }
            style={styles.input}
            placeholder="Ej: mostachos.mp"
            disabled={disabled}
          />
        </label>

        <section style={styles.locationPicker}>
          <div style={styles.locationHeader}>
            <div style={styles.locationHeaderCopy}>
              <strong style={styles.paymentGroupTitle}>
                Ubicacion del local en Google Maps
              </strong>
              <span style={styles.helperText}>
                Busca el domicilio o usa la ubicacion del dispositivo. Mozzo
                completara ciudad, provincia y coordenadas automaticamente.
              </span>
            </div>
            <span
              style={{
                ...styles.locationStatus,
                ...(branchCoordinates
                  ? styles.locationStatusReady
                  : styles.locationStatusPending),
              }}
            >
              {branchCoordinates ? "Ubicacion definida" : "Falta ubicar"}
            </span>
          </div>

          <form style={styles.locationSearchRow} onSubmit={handleSearchBranchLocation}>
            <input
              type="search"
              value={locationQuery}
              onChange={(event) => setLocationQuery(event.target.value)}
              style={styles.input}
              placeholder="Ej: Catamarca 1378, Corrientes, Argentina"
              aria-label="Direccion del local para buscar en Google Maps"
              disabled={disabled || locatingBranch}
            />
            <button
              type="submit"
              style={styles.primaryButton}
              disabled={disabled || locatingBranch}
            >
              {locatingBranch ? "Buscando..." : "Buscar en Maps"}
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={handleUseCurrentLocation}
              disabled={disabled || locatingBranch}
            >
              Usar mi ubicacion
            </button>
          </form>

          {locationMessage ? (
            <div
              style={{
                ...styles.locationFeedback,
                ...(locationTone === "success"
                  ? styles.noticeOk
                  : locationTone === "error"
                    ? styles.noticeError
                    : styles.noticeNeutral),
              }}
            >
              {locationMessage}
            </div>
          ) : null}

          <div style={styles.locationMapLayout}>
            <div style={styles.locationMapFrame}>
              {googleMapsEmbedUrl ? (
                <iframe
                  title={`Ubicacion de ${branchName}`}
                  src={googleMapsEmbedUrl}
                  style={styles.locationMapIframe}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              ) : (
                <div style={styles.locationMapEmpty}>
                  Escribe la direccion para ver el local en el mapa.
                </div>
              )}
            </div>

            <div style={styles.locationSummary}>
              <span style={styles.branchBadgeLabel}>Direccion seleccionada</span>
              <strong style={styles.locationAddress}>
                {profile?.direccion || branch?.address || "Todavia sin direccion"}
              </strong>
              <span style={styles.helperText}>
                {[profile?.paymentLocationCity, profile?.paymentLocationState]
                  .filter(Boolean)
                  .join(", ") || "Ciudad y provincia pendientes"}
              </span>
              {branchCoordinates ? (
                <span style={styles.locationCoordinates}>
                  {branchCoordinates.latitude.toFixed(6)}, {" "}
                  {branchCoordinates.longitude.toFixed(6)}
                </span>
              ) : null}
              <a
                href={googleMapsExternalUrl}
                target="_blank"
                rel="noreferrer"
                style={styles.locationExternalLink}
              >
                Abrir en Google Maps
              </a>
            </div>
          </div>

          <details style={styles.locationTechnicalDetails}>
            <summary style={styles.locationTechnicalSummary}>
              Ver o corregir coordenadas manualmente
            </summary>
            <div style={styles.locationTechnicalGrid}>
              <label style={styles.field}>
                <span style={styles.label}>Ciudad</span>
                <input
                  type="text"
                  value={profile?.paymentLocationCity || ""}
                  onChange={(event) =>
                    updateDraft((prev) => ({
                      ...prev,
                      paymentLocationCity: event.target.value,
                    }))
                  }
                  style={styles.input}
                  disabled={disabled}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>Provincia / estado</span>
                <input
                  type="text"
                  value={profile?.paymentLocationState || ""}
                  onChange={(event) =>
                    updateDraft((prev) => ({
                      ...prev,
                      paymentLocationState: event.target.value,
                    }))
                  }
                  style={styles.input}
                  disabled={disabled}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>Latitud</span>
                <input
                  type="text"
                  value={profile?.paymentLocationLatitude || ""}
                  onChange={(event) =>
                    updateDraft((prev) => ({
                      ...prev,
                      paymentLocationLatitude: event.target.value,
                    }))
                  }
                  style={styles.input}
                  disabled={disabled}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>Longitud</span>
                <input
                  type="text"
                  value={profile?.paymentLocationLongitude || ""}
                  onChange={(event) =>
                    updateDraft((prev) => ({
                      ...prev,
                      paymentLocationLongitude: event.target.value,
                    }))
                  }
                  style={styles.input}
                  disabled={disabled}
                />
              </label>
            </div>
          </details>
        </section>

        <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
          <span style={styles.label}>Referencia interna del kiosk (opcional)</span>
          <input
            type="text"
            value={profile?.paymentLocationReference || ""}
            onChange={(event) =>
              updateDraft((prev) => ({
                ...prev,
                paymentLocationReference: event.target.value,
              }))
            }
            style={styles.input}
            placeholder="Ej: Salon principal, junto a la barra"
            disabled={disabled}
          />
          <span style={styles.helperText}>
            Este dato no modifica la direccion del local en Google Maps.
          </span>
        </label>
      </div>

      <div
        style={{
          ...styles.notice,
          ...(kioskPaymentSetup.kioskQrReady ||
          kioskPaymentSetup.kioskDigitalConfigured
            ? styles.noticeOk
            : styles.noticeError),
          marginTop: "18px",
        }}
      >
        {kioskPaymentSetup.kioskQrReady
          ? "Cobro digital del kiosk listo. El totem ya puede mostrar un QR para abrir el checkout en el celular del cliente."
          : kioskPaymentSetup.kioskDigitalConfigured
            ? kioskPaymentSetup.kioskQrBlockingReason ||
              "La cuenta del restaurant ya esta conectada. Falta un ultimo ajuste para terminar de habilitar el checkout del kiosk."
            : kioskPaymentSetup.kioskQrBlockingReason ||
              "Todavia falta completar la configuracion del cobro digital para el kiosk."}
      </div>

      <div style={styles.paymentMethodsLayout}>
        <div style={styles.paymentMethodsGroup}>
          <strong style={styles.paymentGroupTitle}>Metodos visibles en kiosk</strong>
          <span style={styles.helperText}>
            Esto define si el totem muestra pagar en caja y/o pagar con QR.
          </span>
          <div style={styles.paymentOptionList}>
            {KIOSK_PAYMENT_METHOD_OPTIONS.map((option) => {
              const checked = normalizedKioskPaymentMethods.includes(option.id);

              return (
                <label key={option.id} style={styles.paymentOptionRow}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePaymentMethod("kiosk", option.id)}
                    disabled={disabled}
                  />
                  <div style={styles.paymentOptionCopy}>
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div style={styles.paymentMethodsGroup}>
          <strong style={styles.paymentGroupTitle}>Metodos visibles en Menu QR</strong>
          <span style={styles.helperText}>
            Esto define si el Menu QR muestra pagar despues y/o pago digital inmediato
            desde el celular del comensal.
          </span>
          <div style={styles.paymentOptionList}>
            {QR_PAYMENT_METHOD_OPTIONS.map((option) => {
              const checked = normalizedQrPaymentMethods.includes(option.id);

              return (
                <label key={option.id} style={styles.paymentOptionRow}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePaymentMethod("qr", option.id)}
                    disabled={disabled}
                  />
                  <div style={styles.paymentOptionCopy}>
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </div>
                </label>
              );
            })}
          </div>
          <div
            style={{
              ...styles.notice,
              ...(kioskPaymentSetup.qrDigitalConfigured
                ? styles.noticeOk
                : styles.noticeError),
            }}
          >
            {kioskPaymentSetup.qrDigitalConfigured
              ? "Menu QR listo: el comensal ya puede ver 'Pagar ahora' y abrir el checkout desde su celular."
              : kioskPaymentSetup.qrDeepLinkBlockingReason ||
                "Todavia falta completar la configuracion digital del Menu QR."}
          </div>
        </div>
      </div>

      <div style={styles.actionsRow}>
        <button
          type="button"
          style={styles.primaryButton}
          onClick={handleSavePaymentSettings}
          disabled={
            disabled ||
            savingSettings ||
            connectingMercadoPago ||
            disconnectingMercadoPago
          }
        >
          {savingSettings ? "Guardando cobros..." : "Guardar cobros"}
        </button>
      </div>
    </article>
  );
}

const styles = {
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    padding: "24px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
    display: "grid",
    gap: "18px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  headerCopy: {
    display: "grid",
    gap: "8px",
    minWidth: "280px",
    flex: "1 1 520px",
  },
  panelTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "28px",
    lineHeight: 1.1,
  },
  helperText: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
    display: "block",
    lineHeight: 1.55,
  },
  branchBadge: {
    minWidth: "220px",
    padding: "16px 18px",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#f8fafc",
    display: "grid",
    gap: "6px",
  },
  branchBadgeLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  branchBadgeValue: {
    color: "#0f172a",
    fontSize: "18px",
  },
  notice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    borderRadius: "12px",
    padding: "12px 14px",
    fontWeight: 600,
  },
  mzPurchaseButton: {
    border: "1px solid currentColor",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    color: "inherit",
    padding: "9px 13px",
    fontWeight: 800,
    cursor: "pointer",
  },
  noticeNeutral: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  noticeOk: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeWarning: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
  },
  noticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  paymentConnectionCard: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "14px",
    alignItems: "center",
    padding: "16px 18px",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(135deg, rgba(248,250,252,0.96) 0%, rgba(239,246,255,0.82) 100%)",
  },
  paymentConnectionCopy: {
    display: "grid",
    gap: "8px",
  },
  paymentConnectionActions: {
    display: "grid",
    gap: "10px",
    justifyItems: "end",
    alignSelf: "center",
  },
  paymentGroupTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  paymentConnectionStatus: {
    color: "#334155",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.5,
  },
  secondaryButton: {
    border: "1px solid #bfdbfe",
    borderRadius: "10px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "10px 12px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  miniDangerButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    padding: "10px 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  oauthQrPanel: {
    display: "grid",
    gap: "16px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #bfdbfe",
    background:
      "linear-gradient(135deg, rgba(239,246,255,0.96) 0%, rgba(248,250,252,0.98) 100%)",
  },
  oauthQrCopy: {
    display: "grid",
    gap: "8px",
  },
  oauthQrBody: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, auto) minmax(220px, 1fr)",
    gap: "18px",
    alignItems: "center",
  },
  oauthQrCanvas: {
    width: "fit-content",
    padding: "16px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
  },
  oauthQrActions: {
    display: "grid",
    gap: "10px",
    justifyItems: "start",
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    color: "#334155",
    fontWeight: 700,
    fontSize: "14px",
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  locationPicker: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #bfdbfe",
    background:
      "linear-gradient(135deg, rgba(239,246,255,0.9) 0%, rgba(255,247,237,0.78) 100%)",
  },
  locationHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },
  locationHeaderCopy: {
    display: "grid",
    gap: "6px",
    minWidth: "240px",
    flex: "1 1 520px",
  },
  locationStatus: {
    borderRadius: "999px",
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  locationStatusReady: {
    color: "#166534",
    backgroundColor: "#dcfce7",
    border: "1px solid #86efac",
  },
  locationStatusPending: {
    color: "#9a3412",
    backgroundColor: "#ffedd5",
    border: "1px solid #fdba74",
  },
  locationSearchRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "10px",
    alignItems: "stretch",
  },
  locationFeedback: {
    borderRadius: "12px",
    padding: "11px 13px",
    fontSize: "13px",
    fontWeight: 700,
  },
  locationMapLayout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
    alignItems: "stretch",
  },
  locationMapFrame: {
    minHeight: "280px",
    overflow: "hidden",
    borderRadius: "14px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#e2e8f0",
  },
  locationMapIframe: {
    display: "block",
    width: "100%",
    height: "100%",
    minHeight: "280px",
    border: 0,
  },
  locationMapEmpty: {
    minHeight: "280px",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    textAlign: "center",
    color: "#64748b",
    fontWeight: 700,
  },
  locationSummary: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "10px",
    padding: "18px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "rgba(255,255,255,0.92)",
  },
  locationAddress: {
    color: "#0f172a",
    fontSize: "16px",
    lineHeight: 1.45,
  },
  locationCoordinates: {
    color: "#475569",
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  locationExternalLink: {
    marginTop: "auto",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 800,
    textDecoration: "none",
  },
  locationTechnicalDetails: {
    borderTop: "1px solid rgba(148,163,184,0.45)",
    paddingTop: "12px",
  },
  locationTechnicalSummary: {
    color: "#475569",
    fontSize: "13px",
    fontWeight: 800,
    cursor: "pointer",
  },
  locationTechnicalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "12px",
    paddingTop: "14px",
  },
  paymentMethodsLayout: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "18px",
  },
  paymentMethodsGroup: {
    display: "grid",
    gap: "10px",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#fbfdff",
  },
  paymentOptionList: {
    display: "grid",
    gap: "10px",
  },
  paymentOptionRow: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: "10px",
    alignItems: "start",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  paymentOptionCopy: {
    display: "grid",
    gap: "4px",
    color: "#334155",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    padding: "12px 18px",
    cursor: "pointer",
  },
};

export default BranchDigitalPaymentsPanel;
